<template>
    <div>
        <b-row>
            <b-col>
                <b>Receipt:</b>
                <b-badge v-if="hasUrgentPrescription" variant="danger" class="ml-2">URGENT</b-badge>
                <br>
                <span>{{ order.patient.displayNamesForPerson() }}</span><br>
                <span>{{ value.fullAddress }}</span><br>
            </b-col>
            <b-col>
                <b>Verified: </b> <span>{{ value.verified | formatDate('LLL') }}</span><br>
                <b>Verified By: </b> <span>{{ value.verifiedBy }}</span><br>
                <b>Shipped: </b> <span>{{ value.shipped | formatDate('LLL') }}</span><br>
                <b>Shipped By: </b> <span>{{ value.shippedBy }}</span><br>
            </b-col>
        </b-row>

        <b-row v-if="order.promisedDate" class="mt-2">
            <b-col>
                <b>Promise Date: </b>
                <promised-time-summary :value="order.promisedDate" info-only />
                <span class="ml-1">{{ order.promisedDate | formatDate('LLL') }}</span>
            </b-col>
        </b-row>

        <h4>Scripts</h4>
        <b-row>
            <b-col>
                <!-- <table></table> -->
                <b-table :items="prescriptions" :fields="rxFields"></b-table>
            </b-col>
        </b-row>

        <h4>Add Ons</h4>
        <b-row>
            <b-col>
                <!-- <table></table> -->
                <b-table :items="order.items" :fields="otcFields"></b-table>
            </b-col>
        </b-row>

        <b-row>
            <b-col>
                <b>Tracking Number:</b><br>
                <span>{{ value.trackingNumber }}</span>
            </b-col>
            <b-col>
                <b>Ship Date:</b><br>
                <span>{{ value.verified | formatDate('LL') }}</span>
            </b-col>
            <b-col>
                <b>Shipping Station:</b><br>
                <span>{{ value.shippingStation }}</span>
            </b-col>
        </b-row>

        <b-row v-if="value && value.deliveryConfirmationDate">
            <b-col>
                <strong>Patient Delivery Attestation</strong><br />
                {{ value.deliveryConfirmationAttestationLanguage }}<br />
                <div v-html="signatureHtml" />
                <br />
                <!--            <b-img :src="value.deliveryConfirmationSignature" alt="Signature"/><br/>-->
                {{ order.patient.firstName }} {{ order.patient.lastName }}<br />
                Date Signed: {{ value.deliveryConfirmationDate | formatLocalDate('LLL') }}
            </b-col>
        </b-row>

        <div class="d-flex mt-4" style="gap: 10px;">
            <!--    Only display the send tracking text for orders that are not Will Call orders    -->
            <b-button v-if="value.shipmentType !== 'Will-Call'" @click="sendTrackingText(value.trackingUrl)"
                variant="info">
                <i class="fa fa-paper-plane"></i>
                Send Tracking Text
            </b-button>

            <b-button @click="openDeliveryConfirmationImage" v-if="value.deliveryProofImageId">
                <i class="fa fa-download"></i>
                Download Delivery Confirmation
            </b-button>

            <b-button
                v-if="value.voided && $permissions.some(p => p.permissionCodeName === 'Admin/Unvoid Shipment')"
                @click="unvoidShipment"
                variant="warning">
                <i class="fa fa-undo"></i>
                Unvoid Shipment
            </b-button>
        </div>
    </div>
</template>

<script lang="ts">

import { Component, Prop, Vue } from 'vue-property-decorator';
import { Shipment } from '@/models/Shipment';
import { Order } from '@/models/Order'
import moment from "moment";
import numeral from "numeral";
import { Patient } from "@/models/Patient";
import axios from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import PromisedTimeSummary from '@/components/Order/PromisedTimeSummaryComponent.vue';

@Component({
    components: { PromisedTimeSummary }
})
export default class ShippingTicketComponent extends Vue {

    @Prop() private value!: Shipment;
    private order: Order = new Order();

    @Prop({ required: true }) patient!: Patient;

    private rxFields: any[] = [
        { key: "storeName", label: "Store" },
        { key: "rxId", label: "Rx" },
        { key: "productNameShort", label: "Drug" },
        { key: "prescriberName", label: "Prescriber" },
        { key: "dispensed", label: "Qty", class: "text-center", },
        { key: "patientPay", label: "Copay", class: "text-center", formatter: this.moneyFormatter },
        { key: "fillDate", label: "Fill Date", formatter: this.dateFormatter },
        { key: "insurance", label: "PlanNames" },
    ];

    private otcFields: any[] = [
        { key: "otcItemName", label: "Product Name", },
        { key: "otcItemUPC", label: "UPC", },
        { key: "quantity", label: "Qty", },
        { key: "price", label: "Price", formatter: this.moneyFormatter },
        { key: "tax", label: "Tax", },
    ];

    created() {
        this.getOrderForShipping();
    }

    get hasUrgentPrescription(): boolean {
        return this.order?.prescriptions?.some(rx => rx.isUrgent) ?? false;
    }

    async getOrderForShipping() {
        try {
            const response = await axios.get<Order>(`/Shipping/order/${this.value.id}`);
            this.order = new Order(response.data);
        } catch (error) {
            console.error("Error while loading order information in shipping ticket", { error, response: (error as any)?.response });
            this.$notification(NotificationOptions.error(error));
        }
    }

    dateFormatter(value: Date, _key: string, _item: any) {
        return moment.utc(value).local().format("L");
    }

    moneyFormatter(value: number) {
        return numeral(value).format('$0,0.00');
    }

    get signatureHtml() {
        const sigData = this.value.deliveryConfirmationSignature;
        if (sigData != null && sigData.startsWith("<")) {
            return sigData;
        }
        return `<img src="${sigData}" alt="Signature"/>`;
    }

    get prescriptions(): Array<any> {
        if (!this.order?.prescriptions?.length) return [];

        return this.order.prescriptions.map(rx => {
            const res: any = rx;
            res.insurance = rx.claims?.length ? rx.claims[0].planName : ''; //Showing 1st claim plan name
            res.prescriberName = `${rx.prescriber.lastName}, ${rx.prescriber.firstName}`;
            return res;
        });
    }

    async sendTrackingText(urlString: String) {

        const payloadData = {
            MessageBody: urlString,
            Shipment: this.value,
            Patient: this.patient,
        };

        try {
            await axios.post('/Patient/SendPatientTrackingText', payloadData);
            this.$notification(NotificationOptions.success("Tracking Message Sent"));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async openDeliveryConfirmationImage() {
        const imageId = this.value.deliveryProofImageId;
        if (!imageId) return;
        try {
            const imageBlob = await axios.get(`/api/Image/${imageId}`, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(imageBlob.data);
            window.open(objectUrl, "_blank");
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async unvoidShipment() {
        const confirmed = await this.$bvModal.msgBoxConfirm(
            'Are you sure you want to unvoid this shipment? This will restore the shipment to its previous state.',
            {
                title: 'Confirm Unvoid Shipment',
                okVariant: 'warning',
                okTitle: 'Unvoid',
                cancelTitle: 'Cancel',
                centered: true
            }
        );

        if (!confirmed) {
            return;
        }

        try {
            await axios.post(`/Shipping/UnvoidShipment/${this.value.id}`);
            this.$notification(NotificationOptions.success("Shipment unvoided successfully"));
            this.value.voided = false;
            this.$emit('shipment-unvoided', this.value);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }
}

</script>
